import React from 'react';
import type {
  EdgeDashStyle,
  SequenceMessage,
  SequenceMessageType,
  SequenceParticipant,
  ThemePalette,
} from '../../types';
import { EDGE } from '../../constants/edges';

const DASH_OPTIONS: ReadonlyArray<{ key: EdgeDashStyle; label: string; title: string }> = [
  { key: 'solid', label: '\u2500', title: 'Solid' },
  { key: 'dashed', label: '\u254c', title: 'Dashed' },
  { key: 'dotted', label: '\u22ef', title: 'Dotted' },
  { key: 'dash-dot', label: '\u2500\u00b7', title: 'Dash-dot' },
  { key: 'long-dash', label: '\u254d', title: 'Long dash' },
];

const COLOR_PRESETS: ReadonlyArray<string> = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#ec4899',
  '#64748b',
];

export interface MessageInfoPanelProps {
  palette: Pick<ThemePalette, 'panelBg' | 'panelBorder' | 'panelText' | 'panelSubText' | 'edgeDefault'>;
  glass: React.CSSProperties;
  message: SequenceMessage;
  participants: SequenceParticipant[];
  onClose: () => void;
  onUpdateMessage: (id: string, updates: Partial<SequenceMessage>) => void;
  onRemoveMessage: (id: string) => void;
  /** Take an undo snapshot before any mutation. */
  snapshotBeforeMutation: () => void;
}

/**
 * Read/edit panel for a sequence-diagram `SequenceMessage`.
 *
 * Lets the user change the message kind (sync/async/return), line style and thickness,
 * label, color, description, payload, tooltip, constraints, links, and delete the message.
 */
export const MessageInfoPanel: React.FC<MessageInfoPanelProps> = ({
  palette,
  glass,
  message: infoMsg,
  participants,
  onClose,
  onUpdateMessage,
  onRemoveMessage,
  snapshotBeforeMutation,
}) => {
  const srcP = participants.find((p) => p.id === infoMsg.sourceId);
  const tgtP = participants.find((p) => p.id === infoMsg.targetId);
  const infoMsgStrokeWidth = infoMsg.strokeWidth ?? EDGE.DEFAULT_SEQUENCE_MESSAGE_STROKE_WIDTH;
  const infoMsgDashStyle: EdgeDashStyle =
    infoMsg.dashStyle ?? (infoMsg.type === 'return' ? 'dashed' : 'solid');
  const accentColor = infoMsg.color ?? palette.edgeDefault;

  return (
    <div
      className="absolute right-4 top-16 bottom-16 z-50 w-80 flex flex-col rounded-2xl shadow-2xl overflow-hidden border backdrop-blur-md"
      style={glass}
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: palette.panelBorder }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold truncate" style={{ color: palette.panelText }}>
            {srcP?.label ?? '?'} → {tgtP?.label ?? '?'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="transition-colors ml-2 flex-shrink-0"
          style={{ color: palette.panelSubText }}
        >
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Type <span className="normal-case opacity-60 ml-1">= to cycle</span>
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{
              color: palette.panelText,
              borderColor: palette.panelBorder,
              background: palette.panelBg,
            }}
            value={infoMsg.type}
            onChange={(e) =>
              onUpdateMessage(infoMsg.id, { type: e.target.value as SequenceMessageType })
            }
          >
            <option value="sync">Sync (→)</option>
            <option value="async">Async (⇢)</option>
            <option value="return">Return (⇠)</option>
          </select>
        </div>

        <div>
          <div
            className="text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Line <span className="normal-case opacity-60 ml-1">- to cycle</span>
          </div>
          <div className="flex gap-1">
            {DASH_OPTIONS.map(({ key, label, title }) => {
              const active = infoMsgDashStyle === key;
              return (
                <button
                  key={key}
                  title={title}
                  className="flex-1 py-1 rounded text-xs font-bold transition-all"
                  style={{
                    background: active ? `${accentColor}25` : 'transparent',
                    color: active ? accentColor : palette.panelSubText,
                    border: `1px solid ${active ? accentColor + '80' : palette.panelBorder}`,
                  }}
                  onClick={() => onUpdateMessage(infoMsg.id, { dashStyle: key })}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div
            className="text-xs uppercase tracking-wider mb-1.5 flex items-center justify-between"
            style={{ color: palette.panelSubText }}
          >
            <span>
              Thickness <span className="normal-case opacity-60 ml-1">Ctrl/Cmd +/-</span>
            </span>
            <span className="text-[10px] font-mono" style={{ color: palette.panelText }}>
              {infoMsgStrokeWidth}px
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              className="w-6 h-6 rounded text-xs font-bold flex items-center justify-center transition-colors hover:bg-white/10"
              style={{ color: palette.panelSubText, border: `1px solid ${palette.panelBorder}` }}
              onClick={() => {
                const next =
                  [...EDGE.STROKE_STEPS].reverse().find((step) => step < infoMsgStrokeWidth) ??
                  EDGE.STROKE_STEPS[0];
                onUpdateMessage(infoMsg.id, { strokeWidth: next });
              }}
            >
              -
            </button>
            <div className="flex-1 flex items-center gap-0.5 h-6">
              {EDGE.STROKE_STEPS.map((step) => (
                <button
                  key={`${infoMsg.id}-stroke-${step}`}
                  title={`${step}px`}
                  className="flex-1 rounded-sm transition-all hover:opacity-100"
                  style={{
                    height: Math.max(2, step * 1.2),
                    background: step <= infoMsgStrokeWidth ? accentColor : palette.panelBorder,
                    opacity: step <= infoMsgStrokeWidth ? 1 : 0.3,
                  }}
                  onClick={() => onUpdateMessage(infoMsg.id, { strokeWidth: step })}
                />
              ))}
            </div>
            <button
              className="w-6 h-6 rounded text-xs font-bold flex items-center justify-center transition-colors hover:bg-white/10"
              style={{ color: palette.panelSubText, border: `1px solid ${palette.panelBorder}` }}
              onClick={() => {
                const next =
                  EDGE.STROKE_STEPS.find((step) => step > infoMsgStrokeWidth) ??
                  EDGE.STROKE_STEPS[EDGE.STROKE_STEPS.length - 1];
                onUpdateMessage(infoMsg.id, { strokeWidth: next });
              }}
            >
              +
            </button>
          </div>
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Label
          </label>
          <input
            type="text"
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            value={infoMsg.label}
            onChange={(e) => onUpdateMessage(infoMsg.id, { label: e.target.value })}
          />
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Color
          </label>
          <div className="flex flex-wrap gap-2 items-center">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                style={{
                  backgroundColor: c,
                  borderColor: infoMsg.color === c ? '#fff' : 'transparent',
                }}
                onClick={() => onUpdateMessage(infoMsg.id, { color: c })}
              />
            ))}
            {infoMsg.color && (
              <button
                className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-white/5"
                style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                onClick={() => onUpdateMessage(infoMsg.id, { color: undefined })}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Description
          </label>
          <textarea
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            rows={3}
            placeholder="Describe this message..."
            value={infoMsg.description ?? ''}
            onChange={(e) =>
              onUpdateMessage(infoMsg.id, { description: e.target.value || undefined })
            }
          />
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Payload
          </label>
          <textarea
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            rows={3}
            placeholder='{"key": "value"}'
            value={infoMsg.payload ?? ''}
            onChange={(e) => onUpdateMessage(infoMsg.id, { payload: e.target.value || undefined })}
          />
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Tooltip
          </label>
          <textarea
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            rows={2}
            placeholder="Hover tooltip text (leave blank for none)..."
            value={infoMsg.tooltip ?? ''}
            onChange={(e) => onUpdateMessage(infoMsg.id, { tooltip: e.target.value || undefined })}
          />
          <p
            className="text-[10px] mt-1"
            style={{ color: palette.panelSubText, opacity: 0.6 }}
          >
            Shown in info panel when hovering the message
          </p>
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Constraints
          </label>
          <textarea
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            rows={2}
            placeholder="Pre/post conditions..."
            value={infoMsg.constraints ?? ''}
            onChange={(e) =>
              onUpdateMessage(infoMsg.id, { constraints: e.target.value || undefined })
            }
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label
              className="text-xs uppercase tracking-wider"
              style={{ color: palette.panelSubText }}
            >
              Links
            </label>
            <button
              onClick={() =>
                onUpdateMessage(infoMsg.id, {
                  links: [...(infoMsg.links ?? []), { label: '', url: '' }],
                })
              }
              className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-white/5"
              style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
            >
              + Add
            </button>
          </div>
          {(infoMsg.links ?? []).map((lnk, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input
                type="text"
                placeholder="Label"
                className="flex-1 bg-white/5 border rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-500/60"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                value={lnk.label}
                onChange={(e) =>
                  onUpdateMessage(infoMsg.id, {
                    links: (infoMsg.links ?? []).map((link, idx) =>
                      idx === i ? { ...link, label: e.target.value } : link,
                    ),
                  })
                }
              />
              <input
                type="text"
                placeholder="URL"
                className="flex-1 bg-white/5 border rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-500/60"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                value={lnk.url}
                onChange={(e) =>
                  onUpdateMessage(infoMsg.id, {
                    links: (infoMsg.links ?? []).map((link, idx) =>
                      idx === i ? { ...link, url: e.target.value } : link,
                    ),
                  })
                }
              />
              <button
                onClick={() =>
                  onUpdateMessage(infoMsg.id, {
                    links: (infoMsg.links ?? []).filter((_, idx) => idx !== i),
                  })
                }
                className="text-xs text-red-400 hover:text-red-300 px-1"
              >
                x
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={() => {
            snapshotBeforeMutation();
            onRemoveMessage(infoMsg.id);
            onClose();
          }}
          className="w-full py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors border border-red-500/20"
        >
          Delete Message
        </button>
      </div>
    </div>
  );
};
